import { Link } from "react-router-dom";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import BrandLogo from "@/components/BrandLogo";
import CountrySelect from "@/components/CountrySelect";
import {
  Search, LogOut, User as UserIcon, Briefcase, Globe
} from "lucide-react";
import { toast } from "sonner";

const NAV_ITEMS = [
  { key: "research", icon: Search, labelKey: "nav_research" },
  { key: "cases", icon: Briefcase, labelKey: "nav_cases" },
];

export default function Header({ activeTab, onTabChange }) {
  const { lang, setLang, t } = useLang();
  const { user, logout, updateOperatingCountry } = useAuth();
  const displayUser = user && typeof user === "object" ? user : null;
  const initial = (displayUser?.name || displayUser?.email || "?").trim().charAt(0).toUpperCase();

  const handleCountryChange = async (code) => {
    try {
      await updateOperatingCountry(code);
      toast.success(t("operating_country_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <header className="grid-border-b bg-white sticky top-0 z-30" data-testid="app-header">
      <div className="flex items-center h-16 px-6 gap-6">
        {/* Brand */}
        <Link
          to="/workspace"
          className="flex items-center gap-3 hover:opacity-90 transition-opacity"
          data-testid="brand-block"
        >
          <BrandLogo size="md" alt={t("appName")} />
          <div className="leading-tight">
            <div className="font-serif-display text-lg text-slate-900 tracking-tight">
              {t("appName")}
            </div>
            <div className="eyebrow !text-[9px] !tracking-[0.3em]">{t("appTagline")}</div>
          </div>
        </Link>

        {/* Nav tabs */}
        <nav className="hidden md:flex items-center ms-4 gap-0 grid-border h-10">
          {NAV_ITEMS.map((item, idx) => {
            const Icon = item.icon;
            const active = activeTab === item.key;
            return (
              <button
                key={item.key}
                data-testid={`nav-${item.key}`}
                onClick={() => onTabChange(item.key)}
                className={[
                  "flex items-center gap-2 px-4 h-full text-sm transition-colors duration-150",
                  idx > 0 ? "grid-border-s" : "",
                  active
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                <Icon size={15} strokeWidth={1.8} />
                <span>{t(item.labelKey)}</span>
              </button>
            );
          })}
        </nav>

        <div className="ms-auto flex items-center gap-3">
          {/* Language toggle — shows the language you will switch to */}
          <Button
            data-testid="lang-toggle"
            variant="outline"
            size="sm"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="rounded-none border-slate-300 h-10 px-3 font-mono-tight text-xs tracking-wide"
            aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
          >
            {lang === "ar" ? "EN" : "AR"}
          </Button>

          {/* User menu */}
          {displayUser && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="h-10 w-10 grid-border bg-slate-900 text-white flex items-center justify-center text-sm font-medium hover:bg-slate-800 transition-colors"
                  aria-label="user menu"
                >
                  {displayUser.picture ? (
                    <img src={displayUser.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    initial
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56 grid-border" data-testid="user-menu">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
                    {displayUser.name || displayUser.email}
                  </div>
                  <div className="text-xs text-slate-500 truncate" data-testid="user-email">
                    {displayUser.email}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-2" data-testid="operating-country-setting">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <Globe size={13} />
                    {t("operating_country")}
                  </div>
                  <CountrySelect
                    value={displayUser.operating_country || "JO"}
                    onChange={handleCountryChange}
                    data-testid="header-country-select"
                  />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="rounded-none cursor-default opacity-70 gap-2">
                  <UserIcon size={13} />
                  <span className="text-xs">{displayUser.user_id}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={logout}
                  className="rounded-none cursor-pointer gap-2 text-red-600 focus:text-red-700"
                >
                  <LogOut size={14} />
                  {lang === "ar" ? "تسجيل الخروج" : "Sign out"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden flex items-center grid-border-t overflow-x-auto">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.key;
          return (
            <button
              key={item.key}
              data-testid={`nav-mobile-${item.key}`}
              onClick={() => onTabChange(item.key)}
              className={[
                "flex items-center gap-1.5 px-4 py-3 text-xs whitespace-nowrap",
                active ? "bg-slate-900 text-white" : "text-slate-600",
              ].join(" ")}
            >
              <Icon size={13} />
              {t(item.labelKey)}
            </button>
          );
        })}
      </div>
    </header>
  );
}
